import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Link,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  FormControl,
  List,
  CardActions,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Badge
} from '@mui/material';
import {
  Settings,
  Error,
  CreditCard,
  Instagram,
  Twitter,
  Facebook,
  Language,
  MusicNote,
  Refresh,
  Speaker,
  Add,
  PlayArrow,
  VolumeOff,
  VolumeUp,
  Sync,
  Stop,
  HelpOutline,
  Schedule,
  Today,
  AccessTime,
  Dashboard as DashboardIcon,
  Podcasts,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../utils/axios';
import { loadStripe } from '@stripe/stripe-js';
import EmptyState from '../common/EmptyState';
import DashboardLayout from '../layout/DashboardLayout';

const VENUE_SECTIONS = [
  { key: 'overview', label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
  { key: 'streams', label: 'Live Streams', icon: <Podcasts fontSize="small" /> },
  { key: 'hardware', label: 'Hardware', icon: <Speaker fontSize="small" /> },
  { key: 'schedule', label: 'Schedule', icon: <Schedule fontSize="small" /> },
];

const VenueDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeStreams, setActiveStreams] = useState([]);
  const [deviceStatus, setDeviceStatus] = useState('disconnected'); // 'connected', 'disconnected', 'error'
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableStreams, setAvailableStreams] = useState([]);
  const [streamsLoading, setStreamsLoading] = useState(true);
  const [streamsError, setStreamsError] = useState('');
  const [availableDjs, setAvailableDjs] = useState([]);
  const [djsLoading, setDjsLoading] = useState(true);
  const [djsError, setDjsError] = useState('');
  const [searchDjTerm, setSearchDjTerm] = useState('');
  const [barixDevices, setBarixDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [newDevice, setNewDevice] = useState({
    name: '',
    ipAddress: '',
    apiKey: '',
    model: 'Exstreamer M400',
    notes: '',
    macAddress: ''
  });
  
  // Add new state for DJ schedules
  const [djSchedules, setDjSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState('');
  
  // Add new states for device ordering
  const [deviceOrderLoading, setDeviceOrderLoading] = useState(false);
  const [showOrderDeviceDialog, setShowOrderDeviceDialog] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    email: '',
    phone: ''
  });

  // Add new state for current date/time
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Shared days array for schedule components
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Function to fetch Barix devices
  const fetchBarixDevices = async () => {
    try {
      console.log('Fetching Barix devices...');
      const response = await api.get('/barix/devices');
      console.log('Barix devices response:', response.data);
      setBarixDevices(response.data);
    } catch (error) {
      console.error('Error fetching Barix devices:', error);
      // Don't set general error state here, just log it
    }
  };

  // Check subscription status
  const checkSubscription = async () => {
    try {
      console.log('Checking subscription status...');
      const response = await api.get('/subscriptions/current');
      console.log('Subscription response:', response.data);
      setSubscription(response.data.subscription);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      // Don't set error here to avoid blocking the UI
    }
  };

  // Fetch user data
  const fetchUserData = async () => {
    try {
      console.log('Fetching user data...');
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (storedUser) {
        console.log('User data found in localStorage');
        setUser(storedUser);
      } else {
        console.error('No user data found in localStorage');
        setError('User data not found.');
      }
    } catch (err) {
      console.error('Failed to load user data:', err);
      setError('Failed to load user data.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available streams
  const fetchAvailableStreams = async () => {
    try {
      console.log('Fetching available streams...');
      setStreamsLoading(true);
      setStreamsError('');
      const response = await api.get('/streams/available');
      console.log('Streams response:', response.data);
      setAvailableStreams(response.data);
    } catch (err) {
      console.error("Stream fetch error:", err);
      setStreamsError(err.response?.data?.error || 'Failed to fetch available streams.');
    } finally {
      setStreamsLoading(false);
    }
  };

  // Function to fetch available DJs
  const fetchAvailableDjs = async () => {
    try {
      console.log('Fetching available DJs...');
      setDjsLoading(true);
      setDjsError('');
      // Add timestamp to prevent caching
      const response = await api.get(`/users/djs?t=${Date.now()}`);
      console.log('DJ profiles loaded:', response.data);
      
      // Debug DJ profile images
      response.data.forEach(dj => {
        console.log(`DJ ${dj.name} profile image:`, {
          hasProfileImage: !!dj.djDetails?.profileImage,
          profileImagePath: dj.djDetails?.profileImage,
          completeUrl: dj.djDetails?.profileImage ? 
            `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000'}${dj.djDetails.profileImage}` : 
            null
        });
      });
      
      setAvailableDjs(response.data);
    } catch (err) {
      console.error("DJ fetch error:", err);
      setDjsError(err.response?.data?.error || 'Failed to fetch available DJs.');
    } finally {
      setDjsLoading(false);
    }
  };

  // Function to fetch DJs scheduled at this venue
  const fetchDJSchedules = async () => {
    try {
      console.log('Fetching DJ schedules for venue...');
      setSchedulesLoading(true);
      setSchedulesError('');
      // Add timestamp to prevent caching
      const response = await api.get(`/users/venue/dj-schedule?t=${Date.now()}`);
      console.log('DJ schedules loaded:', response.data);
      setDjSchedules(response.data);
    } catch (err) {
      console.error("DJ schedules fetch error:", err);
      setSchedulesError(err.response?.data?.error || 'Failed to fetch DJ schedules.');
    } finally {
      setSchedulesLoading(false);
    }
  };

  // Function to update current time
  const updateCurrentTime = () => {
    setCurrentDateTime(new Date());
  };

  useEffect(() => {
    console.log('VenueDashboard - mounting component');
    
    // Initialize socket connection
    try {
      const socketUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      console.log('Socket connecting to:', socketUrl);
      
      const newSocket = io(socketUrl, {
        auth: {
          token: localStorage.getItem('token')
        }
      });

      newSocket.on('connect', () => {
        console.log('Socket connected to server');
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      newSocket.on('new-stream', (stream) => {
        console.log('New stream available:', stream);
        fetchAvailableStreams();
      });

      newSocket.on('stream-ended', (streamId) => {
        console.log('Stream ended:', streamId);
        fetchAvailableStreams();
      });

      newSocket.on('device-status', (status) => {
        console.log('Socket event: device-status', status);
        setDeviceStatus(status);
      });
      
      // Add Barix status listeners
      newSocket.on('barix_status_update', (data) => {
        console.log('Socket event: barix_status_update', data);
        setBarixDevices(prevDevices => 
          prevDevices.map(device => 
            device._id === data.deviceId 
              ? {...device, lastStatus: data.status, currentStreamUrl: data.currentStreamUrl}
              : device
          )
        );
      });
      
      newSocket.on('barix_status_error', (data) => {
        console.log('Socket event: barix_status_error', data);
        setBarixDevices(prevDevices => 
          prevDevices.map(device => 
            device._id === data.deviceId 
              ? {...device, lastStatus: {...device.lastStatus, playbackStatus: 'Error'}}
              : device
          )
        );
      });

      // Now that all listeners are attached, set the socket in state
      setSocket(newSocket);

      return () => {
        console.log('Cleaning up socket connection');
        newSocket.disconnect();
      };
    } catch (error) {
      console.error('Socket initialization error:', error);
    }
  }, []); // Empty dependency array - runs once on mount

  // Fetch data on initial load
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        await fetchUserData();
        await checkSubscription();
        await fetchAvailableStreams();
        await fetchAvailableDjs();
        await fetchBarixDevices();
        await fetchDJSchedules();
      } catch (err) {
        console.error('Error in fetchAllData:', err);
      }
    };

    fetchAllData();
    
    // Set up refresh interval
    const intervalId = setInterval(() => {
      console.log('Refreshing data...');
      fetchAvailableStreams();
      fetchBarixDevices();
      fetchDJSchedules();
    }, 30000); // Refresh every 30 seconds

    // Setup interval to update current time every minute
    const timeInterval = setInterval(updateCurrentTime, 60000);

    return () => {
      clearInterval(intervalId);
      clearInterval(timeInterval);
    };
  }, [socket]); // Include socket in the dependency array

  const handleConnectStream = (streamKey) => {
    socket.emit('connect-stream', {
      venueId: JSON.parse(localStorage.getItem('user')).id,
      streamKey
    });
  };

  // Replace handleRegisterDevice with handleOrderDevice for ordering flow
  const handleOrderDevice = async () => {
    try {
      setDeviceOrderLoading(true);
      setOrderError('');
      
      // Create a checkout session for device purchase
      const response = await api.post('/orders/device-checkout', {
        shipping: shippingInfo
      });
      
      // Redirect to Stripe checkout
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });
      
      if (error) {
        setOrderError(error.message);
      }
      
      setDeviceOrderLoading(false);
      setShowOrderDeviceDialog(false);
    } catch (error) {
      console.error('Error ordering device:', error);
      setOrderError(error.response?.data?.message || 'Error processing your order');
      setDeviceOrderLoading(false);
    }
  };
  
  // Handle shipping info input changes
  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Update device status manually
  const handleUpdateStatus = async (deviceId) => {
    try {
      const response = await api.post(`/barix/devices/${deviceId}/status`);
      console.log('Device status updated:', response.data);
      // Refresh devices to show updated status
      fetchBarixDevices();
    } catch (error) {
      console.error('Error updating device status:', error);
      setError('Failed to update device status. ' + (error.response?.data?.message || error.message));
    }
  };
  
  // Set device stream
  const handleSetStream = async (deviceId, streamUrl) => {
    try {
      const response = await api.put(
        `/barix/devices/${deviceId}/stream`,
        { streamUrl }
      );
      console.log('Stream set:', response.data);
      // Refresh devices
      fetchBarixDevices();
    } catch (error) {
      console.error('Error setting stream:', error);
      setError('Failed to set stream. ' + (error.response?.data?.message || error.message));
    }
  };
  
  // Set device volume
  const handleSetVolume = async (deviceId, volume) => {
    try {
      const response = await api.put(
        `/barix/devices/${deviceId}/volume`,
        { volume }
      );
      console.log('Volume set:', response.data);
      
      // Update local state for immediate UI feedback
      setBarixDevices(prevDevices => 
        prevDevices.map(device => 
          device._id === deviceId 
            ? {...device, lastStatus: {...device.lastStatus, volume}}
            : device
        )
      );
    } catch (error) {
      console.error('Error setting volume:', error);
      // For volume changes, avoid showing error toast as it might disrupt user experience
    }
  };
  
  // Toggle device mute
  const handleToggleMute = async (deviceId, currentMuteState) => {
    try {
      const response = await api.put(
        `/barix/devices/${deviceId}/mute`,
        { mute: !currentMuteState }
      );
      console.log('Mute toggled:', response.data);
      
      // Update local state for immediate UI feedback
      setBarixDevices(prevDevices => 
        prevDevices.map(device => 
          device._id === deviceId 
            ? {...device, lastStatus: {...device.lastStatus, muted: !currentMuteState}}
            : device
        )
      );
    } catch (error) {
      console.error('Error toggling mute:', error);
      // For mute changes, avoid showing error toast as it might disrupt user experience
    }
  };

  // Helper function to check if a schedule is upcoming
  const isUpcoming = (schedule) => {
    // Convert schedule day and time to comparable format
    const currentDay = days[currentDateTime.getDay()];
    const currentHour = currentDateTime.getHours();
    const currentMinute = currentDateTime.getMinutes();
    
    // Parse schedule time
    let [startHour, startMinute] = schedule.startTime ? schedule.startTime.split(':').map(Number) : [0, 0];
    
    // Check if schedule is today and starting in the future
    if (schedule.day === currentDay) {
      if (startHour > currentHour || (startHour === currentHour && startMinute > currentMinute)) {
        return true;
      }
    }
    
    // Check if schedule is in the upcoming days (within the next 6 days)
    const currentDayIndex = days.indexOf(currentDay);
    const scheduleDayIndex = days.indexOf(schedule.day);
    
    // Calculate difference considering week wrapping
    const dayDiff = (scheduleDayIndex - currentDayIndex + 7) % 7;
    
    // Return true if the schedule is within the next 6 days (not today)
    return dayDiff > 0 && dayDiff < 7;
  };
  
  // Helper function to format day as relative to current day
  const formatRelativeDay = (scheduleDay) => {
    const currentDay = days[currentDateTime.getDay()];
    
    if (scheduleDay === currentDay) {
      return 'Today';
    }
    
    const currentDayIndex = days.indexOf(currentDay);
    const scheduleDayIndex = days.indexOf(scheduleDay);
    
    // Calculate difference considering week wrapping
    const dayDiff = (scheduleDayIndex - currentDayIndex + 7) % 7;
    
    if (dayDiff === 1) {
      return 'Tomorrow';
    }
    
    return scheduleDay.charAt(0).toUpperCase() + scheduleDay.slice(1);
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
    <DashboardLayout
      title="Venue Dashboard"
      subtitle="Manage your venue and streams"
      sections={VENUE_SECTIONS}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
    >
      {/* ===== OVERVIEW SECTION ===== */}
      {activeSection === 'overview' && (<Box>
      {/* Subscription Alert - Show only if no subscription */}
      {!subscription && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              component={RouterLink} 
              to="/subscription/plans"
              startIcon={<CreditCard />}
            >
              View Plans
            </Button>
          }
        >
          Subscribe to a plan to unlock premium features and support for your venue.
        </Alert>
      )}
      
      {/* Subscription Status - Show if has subscription */}
      {subscription && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CreditCard sx={{ mr: 1 }} />
            <Typography variant="h6">
              Subscription Status
            </Typography>
          </Box>
          <Typography variant="body1">
            You are currently on the <strong>{subscription.planName}</strong> plan.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subscription.cancelAtPeriodEnd 
              ? `Your subscription will end on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}.` 
              : `Next billing date: ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
            }
          </Typography>
          <Button 
            variant="outlined" 
            size="small" 
            component={RouterLink} 
            to="/subscription/plans" 
            sx={{ mt: 2 }}
          >
            Manage Subscription
          </Button>
        </Paper>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      </Box>)}

      {/* ===== SCHEDULE SECTION ===== */}
      {activeSection === 'schedule' && (<Box>
      {/* Set Schedule Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Schedule color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    Set Schedule
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Today fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      {currentDateTime.toLocaleDateString('en-US', { weekday: 'long' })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      {currentDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                  <Tooltip title="Refresh schedules">
                    <IconButton onClick={fetchDJSchedules} size="small">
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {schedulesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : schedulesError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {schedulesError}
                </Alert>
              ) : djSchedules.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  No DJs have scheduled your venue yet. 
                  <br />
                  DJs can add your venue to their schedule from their dashboard.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {/* Show upcoming sets first */}
                  {djSchedules.some(dj => dj.schedules.some(isUpcoming)) && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        Upcoming Sets
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>DJ</TableCell>
                              <TableCell>Day</TableCell>
                              <TableCell>Time</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {djSchedules.flatMap(dj => 
                              dj.schedules
                                .filter(isUpcoming)
                                .map((schedule, index) => (
                                  <TableRow key={`${dj.djId}-upcoming-${index}`}>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                          component="img"
                                          src={dj.profileImage ? `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000'}${dj.profileImage}` : '/static/images/avatar/default.jpg'}
                                          alt={dj.djName}
                                          sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            mr: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                          }}
                                        />
                                        {dj.djName}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={formatRelativeDay(schedule.day)} 
                                        size="small" 
                                        color={schedule.day === days[currentDateTime.getDay()] ? "primary" : "default"}
                                        variant={schedule.day === days[currentDateTime.getDay()] ? "filled" : "outlined"}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {schedule.startTime}{schedule.endTime ? ` - ${schedule.endTime}` : ''}
                                    </TableCell>
                                    <TableCell>
                                      {schedule.notes || '—'}
                                    </TableCell>
                                  </TableRow>
                                ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}
                  
                  {/* All DJs section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, mt: 2 }}>
                      All Scheduled DJs
                    </Typography>
                  </Grid>
                  
                  {djSchedules.map((dj) => (
                    <Grid item xs={12} key={dj.djId}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            component="img"
                            src={dj.profileImage ? `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000'}${dj.profileImage}` : '/static/images/avatar/default.jpg'}
                            alt={dj.djName}
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: '50%',
                              objectFit: 'cover',
                              mr: 2,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {dj.djName}
                          </Typography>
                        </Box>
                        
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Regular Schedule:
                        </Typography>
                        
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Day</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell>Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dj.schedules.map((schedule, index) => (
                                <TableRow 
                                  key={index}
                                  sx={isUpcoming(schedule) ? { 
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  } : {}}
                                >
                                  <TableCell>
                                    {formatRelativeDay(schedule.day)}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.startTime}{schedule.endTime ? ` - ${schedule.endTime}` : ''}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.notes || '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      </Box>)}

      {/* ===== STREAMS SECTION ===== */}
      {activeSection === 'streams' && (<Box>
      {/* Available Streams Section */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        Available Live Streams
      </Typography>
      {streamsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : streamsError ? (
        <Alert severity="error">{streamsError}</Alert>
      ) : (
        <Box>
          {availableStreams.length === 0 ? (
            <EmptyState
              icon={<Podcasts />}
              title="No Live Streams"
              message="No live streams currently available. Check back when DJs are broadcasting."
            />
          ) : (
            <List>
              {availableStreams.map((stream, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                     <Typography variant="h6">{stream.djName || stream.server_name || 'Unnamed Stream'}</Typography>
                     <Typography variant="body2" color="text.secondary" gutterBottom>
                       {stream.djGenre && <span>Genre: {stream.djGenre} • </span>}
                       {stream.server_description || 'No description'} 
                       {stream.serverName && <span> (from {stream.serverName})</span>}
                     </Typography>
                     
                     {/* DJ Social Links */}
                     {stream.djSocialLinks && (
                       <Box sx={{ mt: 1, mb: 2 }}>
                         {stream.djSocialLinks.instagram && (
                           <Tooltip title="Instagram">
                             <IconButton 
                               size="small" 
                               component="a" 
                               href={`https://instagram.com/${stream.djSocialLinks.instagram}`} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               sx={{ mr: 1 }}
                             >
                               <Instagram fontSize="small" />
                             </IconButton>
                           </Tooltip>
                         )}
                         {stream.djSocialLinks.twitter && (
                           <Tooltip title="Twitter">
                             <IconButton 
                               size="small" 
                               component="a" 
                               href={`https://twitter.com/${stream.djSocialLinks.twitter}`} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               sx={{ mr: 1 }}
                             >
                               <Twitter fontSize="small" />
                             </IconButton>
                           </Tooltip>
                         )}
                         {stream.djSocialLinks.facebook && (
                           <Tooltip title="Facebook">
                             <IconButton 
                               size="small" 
                               component="a" 
                               href={stream.djSocialLinks.facebook} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               sx={{ mr: 1 }}
                             >
                               <Facebook fontSize="small" />
                             </IconButton>
                           </Tooltip>
                         )}
                         {stream.djSocialLinks.soundcloud && (
                           <Tooltip title="Soundcloud">
                             <IconButton 
                               size="small" 
                               component="a" 
                               href={stream.djSocialLinks.soundcloud} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               sx={{ mr: 1 }}
                             >
                               <MusicNote fontSize="small" />
                             </IconButton>
                           </Tooltip>
                         )}
                         {stream.djSocialLinks.website && (
                           <Tooltip title="Website">
                             <IconButton 
                               size="small" 
                               component="a" 
                               href={stream.djSocialLinks.website} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               sx={{ mr: 1 }}
                             >
                               <Language fontSize="small" />
                             </IconButton>
                           </Tooltip>
                         )}
                       </Box>
                     )}
                     
                     {stream.listenurl && (
                        <Button 
                          component={Link}
                          href={stream.listenurl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          LISTEN NOW
                        </Button>
                     )}
                     {barixDevices.length > 0 && (
                       <Button
                         variant="outlined"
                         size="small"
                         onClick={() => handleConnectStream(stream.listenurl)}
                       >
                         {barixDevices.length === 1 ? `Connect to ${barixDevices[0].name}` : "Connect to Barix"}
                       </Button>
                     )}
                  </CardContent>
                </Card>
              ))}
            </List>
          )}
        </Box>
      )}

      </Box>)}

      {/* ===== HARDWARE SECTION ===== */}
      {activeSection === 'hardware' && (<Box>
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Speaker sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Barix Audio Devices
                </Typography>
              </Box>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<Add />}
                onClick={() => setShowOrderDeviceDialog(true)}
              >
                Order Device
              </Button>
            </Box>
            
            {/* Device List */}
            {barixDevices.length === 0 ? (
              <EmptyState
                icon={<Speaker />}
                title="No Devices Registered"
                message="The RetailPlayer M400 is a professional-grade audio device that streams music directly to your venue's sound system."
                ctaText="Order Device"
                onCtaClick={() => setShowOrderDeviceDialog(true)}
              />
            ) : (
              <Grid container spacing={2}>
                {barixDevices.map((device) => (
                  <Grid item xs={12} md={6} key={device._id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {device.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          IP: {device.ipAddress} • Model: {device.model}
                        </Typography>
                        {device.macAddress && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            MAC: {device.macAddress}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Firmware: {device.firmwareVersion || 'Unknown'}
                        </Typography>
                        
                        {/* Status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            Status:
                          </Typography>
                          {device.lastStatus?.playbackStatus === 'Playing' ? (
                            <PlayArrow sx={{ color: 'success.main' }} fontSize="small" />
                          ) : device.lastStatus?.playbackStatus === 'Buffering' ? (
                            <Sync sx={{ color: 'warning.main' }} fontSize="small" />
                          ) : device.lastStatus?.playbackStatus === 'Error' ? (
                            <Error sx={{ color: 'error.main' }} fontSize="small" />
                          ) : (
                            <Stop sx={{ color: 'text.secondary' }} fontSize="small" />
                          )}
                          <Typography
                            variant="body2"
                            sx={{ 
                              ml: 0.5, 
                              color: device.lastStatus?.playbackStatus === 'Playing' 
                                ? 'success.main' 
                                : device.lastStatus?.playbackStatus === 'Buffering'
                                  ? 'warning.main'
                                  : device.lastStatus?.playbackStatus === 'Error'
                                    ? 'error.main'
                                    : 'text.secondary'
                            }}
                          >
                            {device.lastStatus?.playbackStatus || 'Unknown'}
                          </Typography>
                          
                          <Box sx={{ ml: 'auto' }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleUpdateStatus(device._id)}
                              title="Refresh status"
                            >
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        {/* Volume Control */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <IconButton 
                            size="small"
                            onClick={() => handleToggleMute(device._id, device.lastStatus?.muted)}
                          >
                            {device.lastStatus?.muted ? (
                              <VolumeOff fontSize="small" />
                            ) : (
                              <VolumeUp fontSize="small" />
                            )}
                          </IconButton>
                          <Slider
                            size="small"
                            value={device.lastStatus?.volume || 0}
                            onChange={(e, newValue) => handleSetVolume(device._id, newValue)}
                            min={0}
                            max={100}
                            sx={{ mx: 2, flexGrow: 1 }}
                          />
                          <Typography variant="body2">
                            {device.lastStatus?.volume || 0}%
                          </Typography>
                        </Box>
                        
                        {/* Current Stream */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Current Stream:
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {device.currentStreamUrl || 'None'}
                          </Typography>
                        </Box>
                        
                        {/* Stream Controls */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            startIcon={<Settings />}
                            onClick={() => setSelectedDevice(device)}
                          >
                            Settings
                          </Button>
                          
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            disabled={availableStreams.length === 0}
                            startIcon={<PlayArrow />}
                            onClick={() => {
                              if (availableStreams.length > 0) {
                                // Use listenurl from stream object (avoids hardcoded server IP)
                                const streamUrl = availableStreams[0].listenurl || `${process.env.REACT_APP_ICECAST_BASE_URL || ''}/${availableStreams[0].mount}`;
                                handleSetStream(device._id, streamUrl);
                              }
                            }}
                          >
                            Play First Available Stream
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      </Box>)}

    </DashboardLayout>

      {/* Replace the device registration dialog with order dialog */}
      <Dialog
        open={showOrderDeviceDialog}
        onClose={() => setShowOrderDeviceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Order Barix RetailPlayer M400</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            The RetailPlayer M400 is a professional-grade audio device that allows you to stream music 
            directly to your venue's sound system. Fill in your shipping information below to order.
          </DialogContentText>
          
          {orderError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {orderError}
            </Alert>
          )}
          
          <Typography variant="h6" gutterBottom>Shipping Information</Typography>
          
          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            name="name"
            value={shippingInfo.name}
            onChange={handleShippingInfoChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Street Address"
            name="address"
            value={shippingInfo.address}
            onChange={handleShippingInfoChange}
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="City"
                name="city"
                value={shippingInfo.city}
                onChange={handleShippingInfoChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="State/Province"
                name="state"
                value={shippingInfo.state}
                onChange={handleShippingInfoChange}
                required
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="ZIP/Postal Code"
                name="zipCode"
                value={shippingInfo.zipCode}
                onChange={handleShippingInfoChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={shippingInfo.country}
                  onChange={handleShippingInfoChange}
                  label="Country"
                >
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="UK">United Kingdom</MenuItem>
                  <MenuItem value="AU">Australia</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            type="email"
            value={shippingInfo.email}
            onChange={handleShippingInfoChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Phone Number"
            name="phone"
            value={shippingInfo.phone}
            onChange={handleShippingInfoChange}
            required
          />
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>Device Details:</Typography>
            <Typography variant="body2" gutterBottom>RetailPlayer M400</Typography>
            <Typography variant="body2" gutterBottom>Price: $399.00</Typography>
            <Typography variant="body2" gutterBottom>Shipping: Calculated at checkout</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOrderDeviceDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleOrderDevice}
            variant="contained"
            color="primary"
            disabled={deviceOrderLoading || !shippingInfo.name || !shippingInfo.address || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zipCode || !shippingInfo.email}
          >
            {deviceOrderLoading ? <CircularProgress size={24} /> : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Debug information at the bottom if there are errors */}
      {process.env.NODE_ENV === 'development' && (error || streamsError || djsError) && (
        <Paper elevation={3} sx={{ p: 3, mt: 3, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom>Debug Information</Typography>
          
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="error">Main Error:</Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          
          {streamsError && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="error">Streams Error:</Typography>
              <Typography variant="body2">{streamsError}</Typography>
            </Box>
          )}
          
          {djsError && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="error">DJs Error:</Typography>
              <Typography variant="body2">{djsError}</Typography>
            </Box>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">User Object:</Typography>
            <Typography variant="body2" sx={{ overflowX: 'auto' }}>
              {JSON.stringify(user, null, 2)}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">API State:</Typography>
            <Typography variant="body2">
              Streams loaded: {availableStreams.length}
            </Typography>
            <Typography variant="body2">
              Barix devices loaded: {barixDevices.length}
            </Typography>
            <Typography variant="body2">
              DJs loaded: {availableDjs.length}
            </Typography>
          </Box>
        </Paper>
      )}
      
      {/* Device Settings Dialog */}
      <Dialog
        open={Boolean(selectedDevice)}
        onClose={() => setSelectedDevice(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedDevice && (
          <>
            <DialogTitle>Device Settings: {selectedDevice.name}</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                margin="normal"
                label="Device Name"
                name="name"
                value={selectedDevice.name}
                onChange={(e) => setSelectedDevice({
                  ...selectedDevice,
                  name: e.target.value
                })}
              />
              <TextField
                fullWidth
                margin="normal"
                label="IP Address"
                name="ipAddress"
                value={selectedDevice.ipAddress}
                onChange={(e) => setSelectedDevice({
                  ...selectedDevice,
                  ipAddress: e.target.value
                })}
              />
              <TextField
                fullWidth
                margin="normal"
                label="MAC Address"
                name="macAddress"
                value={selectedDevice.macAddress || ''}
                onChange={(e) => setSelectedDevice({
                  ...selectedDevice,
                  macAddress: e.target.value
                })}
                placeholder="00:08:E1:0B:BC:80"
              />
              <TextField
                fullWidth
                margin="normal"
                label="API Key"
                name="apiKey"
                value={selectedDevice.apiKey}
                onChange={(e) => setSelectedDevice({
                  ...selectedDevice,
                  apiKey: e.target.value
                })}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Device Model</InputLabel>
                <Select
                  name="model"
                  value={selectedDevice.model}
                  onChange={(e) => setSelectedDevice({
                    ...selectedDevice,
                    model: e.target.value
                  })}
                  label="Device Model"
                >
                  <MenuItem value="Exstreamer M400">Exstreamer M400</MenuItem>
                  <MenuItem value="RetailPlayer M400">RetailPlayer M400</MenuItem>
                  <MenuItem value="MPA400">MPA400</MenuItem>
                  <MenuItem value="IP Former">IP Former</MenuItem>
                  <MenuItem value="Other">Other Barix Device</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                margin="normal"
                label="Notes"
                name="notes"
                value={selectedDevice.notes || ''}
                onChange={(e) => setSelectedDevice({
                  ...selectedDevice,
                  notes: e.target.value
                })}
                multiline
                rows={2}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedDevice(null)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={async () => {
                  try {
                    await api.delete(`/barix/devices/${selectedDevice._id}`);
                    fetchBarixDevices();
                    setSelectedDevice(null);
                  } catch (error) {
                    console.error('Error deleting device:', error);
                    setError('Failed to delete device: ' + (error.response?.data?.message || error.message));
                  }
                }}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    await api.put(`/barix/devices/${selectedDevice._id}`, {
                      name: selectedDevice.name,
                      ipAddress: selectedDevice.ipAddress,
                      apiKey: selectedDevice.apiKey,
                      model: selectedDevice.model,
                      notes: selectedDevice.notes,
                      macAddress: selectedDevice.macAddress
                    });
                    fetchBarixDevices();
                    setSelectedDevice(null);
                  } catch (error) {
                    console.error('Error updating device:', error);
                    setError('Failed to update device: ' + (error.response?.data?.message || error.message));
                  }
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default VenueDashboard; 